import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { VideoFeatures } from '@/store/videoFeatures/types';
import { ManualSpeedTab } from './speed-testing/tabs/ManualSpeedTab';
import { AutomaticTestTab } from './speed-testing/tabs/AutomaticTestTab';
import { SpeedTestMetricsSubTab } from './speed-testing/tabs/SpeedTestMetricsSubTab';
import { Gauge } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface PlaybackSpeedConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const PlaybackSpeedConfig: React.FC<PlaybackSpeedConfigProps> = ({
  features,
  setFeature,
}) => {
  useLanguage();
  // Estado controlado para as tabs
  const [activeTab, setActiveTab] = React.useState("manual");

  // Check if automatic test is active
  const isAutomaticTestActive = features.speedTests?.some(test => test.enabled && test.isAutomatic) || false;

  return (
    <div className="flex flex-col h-full">
      {/* Header informativo (sem switch) */}
      <div className="px-6 py-4">
        <div
          className={cn(
            "flex items-center gap-3",
            "rounded-[12px] p-3.5",
            "bg-white",
            "border border-[#E4E7EC]"
          )}
        >
          {/* Icon Wrapper */}
          <div
            className={cn(
              "flex-shrink-0 w-9 h-9",
              "rounded-[10px]",
              "flex items-center justify-center",
              "bg-[#EEF4FF]",
              "text-[#1D4FFF]"
            )}
          >
            <Gauge className="w-5 h-5" strokeWidth={2} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h4 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight">
              {translateInterfaceText("Velocidade de Reprodução")}</h4>
            <p className="text-[12px] font-normal text-[#6B7280] leading-snug mt-1">
              {translateInterfaceText("Defina uma velocidade fixa ou rode um teste automático para descobrir a melhor.")}</p>
          </div>
        </div>
      </div>

      {/* Conteúdo das tabs */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6 bg-[#F1F5F9] p-1 rounded-xl">
            <TabsTrigger
              value="manual"
              className="h-9 rounded-lg text-sm data-[state=active]:bg-[#1F6BFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm"
            >
              {translateInterfaceText("Manual")}</TabsTrigger>
            <TabsTrigger
              value="automatic"
              className="h-9 rounded-lg text-sm data-[state=active]:bg-[#1F6BFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm"
            >
              {translateInterfaceText("Automático")}</TabsTrigger>
            <TabsTrigger
              value="metrics"
              className="h-9 rounded-lg text-sm data-[state=active]:bg-[#1F6BFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm"
            >
              {translateInterfaceText("Métricas")}</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ManualSpeedTab
                features={features}
                setFeature={setFeature}
                isAutomaticTestActive={isAutomaticTestActive}
              />
            </motion.div>
          </TabsContent>

          <TabsContent value="automatic" className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <AutomaticTestTab
                features={features}
                setFeature={setFeature}
              />
            </motion.div>
          </TabsContent>

          <TabsContent value="metrics" className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <SpeedTestMetricsSubTab
                features={features}
                setFeature={setFeature}
              />
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
